import React, { useState, useRef, useEffect, useMemo } from 'react';

/**
 * Premium Minimal Searchable Select / Combobox Component
 * Handles instant typeahead search, clean list rendering, and keyboard navigation.
 */
export default function SearchableSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select an option...",
  searchPlaceholder,
  required = false,
  error,
  note,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
      setSearchTerm('');
      setActiveIdx(0);
    }
  }, [isOpen]);

  // Selected Option
  const selectedOption = useMemo(() => {
    return options.find(o => String(o.value) === String(value) && o.value !== "");
  }, [options, value]);

  // Filtered Options
  const filteredOptions = useMemo(() => {
    const selectable = options.filter(o => o.value !== "");
    if (!searchTerm.trim()) return selectable;
    const q = searchTerm.toLowerCase().trim();
    return selectable.filter(o => {
      const optLabel = String(o.label || '').toLowerCase();
      const optVal = String(o.value || '').toLowerCase();
      return optLabel.includes(q) || optVal.includes(q);
    });
  }, [options, searchTerm]);

  const handleSelect = (optVal) => {
    onChange({ target: { value: optVal } });
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(prev => (prev + 1) % Math.max(1, filteredOptions.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(prev => (prev - 1 + filteredOptions.length) % Math.max(1, filteredOptions.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions[activeIdx]) {
        handleSelect(filteredOptions[activeIdx].value);
      }
    }
  };

  return (
    <div className="relative" ref={containerRef} onKeyDown={handleKeyDown}>
      {label && (
        <label className="flex items-center justify-between text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
          <span className="flex items-center gap-1">
            {label} {required && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm transition-all text-left bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 ${
          error
            ? "border-red-500 focus:ring-red-200"
            : isOpen
            ? "border-primary ring-2 ring-primary/20 dark:border-indigo-500"
            : "border-border dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600"
        } ${disabled ? "opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-900" : "cursor-pointer"}`}
      >
        <span className={`truncate font-medium ${selectedOption ? "text-ink dark:text-white" : "text-slate-400 dark:text-slate-500"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="flex items-center gap-1.5 ml-2 text-slate-400">
          {selectedOption && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect("");
              }}
              className="p-0.5 hover:text-slate-600 dark:hover:text-slate-200 rounded"
              title="Clear selection"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )}
          <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180 text-primary" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full min-w-[200px] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Quick Search Bar */}
          <div className="p-2 border-b border-slate-100 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/80">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setActiveIdx(0);
                }}
                placeholder={searchPlaceholder || `Search ${label || 'options'}...`}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-ink dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-primary dark:focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div ref={listRef} className="max-h-56 overflow-y-auto p-1.5 space-y-0.5 custom-scrollbar">
            {filteredOptions.length === 0 ? (
              <div className="py-5 text-center text-xs text-slate-400 dark:text-slate-500">
                No matching options found
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const isSelected = String(opt.value) === String(value);
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs rounded-lg transition-colors text-left ${
                      isSelected
                        ? "bg-primary text-white font-semibold dark:bg-indigo-600 shadow-sm"
                        : isActive
                        ? "bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-white"
                        : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    }`}
                  >
                    <span className="truncate font-medium">{opt.label}</span>
                    {isSelected && (
                      <svg className="w-3.5 h-3.5 text-white shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Quick Footer Count */}
          {filteredOptions.length > 5 && (
            <div className="px-3 py-1 bg-slate-50 dark:bg-slate-900/60 border-t border-slate-100 dark:border-slate-700/50 flex items-center justify-between text-[10px] text-slate-400">
              <span>{filteredOptions.length} available</span>
              <span>Press ↑↓ to navigate</span>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-500 font-medium mt-1">{error}</p>}
      {note && !error && <p className="text-xs text-muted mt-1">{note}</p>}
    </div>
  );
}
